import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePathname } from "next/navigation";
import { useToast } from "@/hooks/ui/useToast";
import { filter } from "lodash";

import { FeedbackThankyou } from "./FeedbackThankyou";
import { MultipleFeedback } from "./MultipleFeedback";
import { AddInfoFeedback } from "./AddInfoFeedback";
import { SingleFeedback } from "./SingleFeedback";
import { FeedbackHero } from "./FeedbackHero";
import { Button } from "../Button";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";

import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";
import {
  FEEDBACK_PREVIEW_INDEX,
  handleSaveAndMore,
  handleSubmit,
} from "@/utils/feedbackPreviewNavigation";

import { QRFeedbackContextProps } from "@/contexts/QRFeedbackContext/types";
import { QRFeedbackContext } from "@/contexts/QRFeedbackContext";

export const FeedbackReviewPreview = ({ data, isInitPreview }) => {
  const { themeColor, categories } = data;

  const { t } = useTranslation("feedback");
  const { toast } = useToast();
  const pathname = usePathname();

  const [isRequesting, setIsRequesting] = React.useState(false);

  const qrFeedbackContextData = useContext<QRFeedbackContextProps>(QRFeedbackContext);
  const { selectedReviewCategory, currentView, setCurrentView, feedbackReviewData } =
    qrFeedbackContextData;

  const hasMultipleCategories = categories?.length > 1;

  const customClass = () => {
    if (currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM) return "multiple";
    if (currentView === FEEDBACK_PREVIEW_INDEX.THANK_YOU) return "thank-you";
    else return "";
  };

  const isSubmitButtonDisabled = () => {
    if (currentView === FEEDBACK_PREVIEW_INDEX.HOME_MULTIPLE_CATEGORIES) {
      return (
        filter(feedbackReviewData.categories, { rating: null }).length ===
        feedbackReviewData.categories?.length
      );
    } else if (currentView === FEEDBACK_PREVIEW_INDEX.HOME_SINGLE_CATEGORY) {
      return (
        selectedReviewCategory?.rating === null &&
        filter(feedbackReviewData.categories, { rating: null }).length > 0
      );
    } else if (currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM) {
      return selectedReviewCategory?.rating === null;
    }
  };

  const hasAddInfoForm = () => {
    const {
      form: { email, name, phone },
    } = data;

    return email || name || phone;
  };

  const onSubmit = async () => {
    try {
      setIsRequesting(true);
      await handleSubmit(qrFeedbackContextData, hasMultipleCategories, pathname, hasAddInfoForm());
      setIsRequesting(false);
    } catch (error) {
      setIsRequesting(false);
      const errorMessage = ApiErrorMessageBuilder.buildToastMessage(error);
      toast({ message: errorMessage, type: "error" });
    }
  };

  useEffect(() => {
    setCurrentView(!hasMultipleCategories ? 0 : 1);
  }, [categories]);

  return (
    <>
      <FeedbackHero
        data={data}
        currentView={currentView}
        isInitPreview={isInitPreview}
      />
      <div className={`feedback-content ${customClass()}`}>
        {currentView === FEEDBACK_PREVIEW_INDEX.HOME_SINGLE_CATEGORY && (
          <SingleFeedback
            data={data}
            isInitPreview={isInitPreview}
            hasMultipleCategories={hasMultipleCategories}
            category={selectedReviewCategory}
          />
        )}
        {currentView === FEEDBACK_PREVIEW_INDEX.HOME_MULTIPLE_CATEGORIES && (
          <MultipleFeedback
            data={data}
            currentView={currentView}
            isInitPreview={isInitPreview}
          />
        )}

        {currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM && (
          <SingleFeedback
            data={data}
            isInitPreview={isInitPreview}
            hasMultipleCategories={hasMultipleCategories}
            category={selectedReviewCategory}
          />
        )}

        {currentView === FEEDBACK_PREVIEW_INDEX.ADD_INFO && (
          <AddInfoFeedback
            data={data}
            hasMultipleCategories={hasMultipleCategories}
          />
        )}

        {currentView === FEEDBACK_PREVIEW_INDEX.THANK_YOU && <FeedbackThankyou data={data} />}
      </div>

      {categories?.length > 0 && (
        <div className="feedback-buttons feedback-button">
          {currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM && (
            <ButtonLib
              shape="ghost"
              size="small"
              label={t("save_more_button_label")}
              onClick={() => handleSaveAndMore(qrFeedbackContextData)}
              dataQA="feedback-submit-button"
              disabled={selectedReviewCategory.rating === 0}
            />
          )}

          <Button
            label={
              currentView === FEEDBACK_PREVIEW_INDEX.THANK_YOU
                ? t("thank_you_button_label")
                : t("send_feedback_button_label")
            }
            type="button"
            onClick={() => onSubmit()}
            dataQA="feedback-submit-button"
            small
            loading={isRequesting}
            buttonColor={themeColor.buttonColor}
            disabled={isSubmitButtonDisabled()}
          />
        </div>
      )}
    </>
  );
};
